import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API } from '@/apiConfig';

const TOKEN_KEY = 'demax_customer_token';

const CustomerContext = createContext(null);

async function apiJson(path, opts = {}) {
  const { skipAuth, body, ...rest } = opts;
  const headers = {
    'Content-Type': 'application/json',
    ...(rest.headers || {}),
  };
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
  if (token && !skipAuth) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API}${path}`, {
    ...rest,
    headers,
    body: body !== undefined ? JSON.stringify(body) : rest.body,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { detail: text || 'Invalid response' };
  }
  if (!res.ok) {
    const msg =
      (data && typeof data.detail === 'string' && data.detail) ||
      (data && typeof data.message === 'string' && data.message) ||
      `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

export function CustomerProvider({ children }) {
  const [token, setTokenState] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));

  const setToken = useCallback((next) => {
    setTokenState(next);
    try {
      if (next) localStorage.setItem(TOKEN_KEY, next);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const refreshMe = useCallback(async () => {
    const t = typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
    if (!t) {
      setUser(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const me = await apiJson('/account/me', { skipAuth: false });
      setUser(me);
      return me;
    } catch {
      setToken(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [setToken]);

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const run = () => {
      if (!cancelled) refreshMe();
    };
    if (typeof window === 'undefined') return undefined;
    const id =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(run, { timeout: 4000 })
        : window.setTimeout(run, 1500);
    return () => {
      cancelled = true;
      if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(id);
      else window.clearTimeout(id);
    };
  }, [token, refreshMe]);

  const login = useCallback(
    async (email, password) => {
      const data = await apiJson('/account/login', {
        method: 'POST',
        body: { email, password },
        skipAuth: true,
      });
      if (data?.token) setToken(data.token);
      if (data?.user) setUser(data.user);
      return data;
    },
    [setToken],
  );

  const register = useCallback(
    async (payload) => {
      const data = await apiJson('/account/register', {
        method: 'POST',
        body: payload,
        skipAuth: true,
      });
      if (data?.token) setToken(data.token);
      if (data?.user) setUser(data.user);
      return data;
    },
    [setToken],
  );

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, [setToken]);

  const value = useMemo(
    () => ({
      token,
      user,
      loading,
      isAuthenticated: Boolean(token && user),
      login,
      register,
      logout,
      refreshMe,
      fetchMyRfqs: () => apiJson('/account/rfqs'),
      fetchCustomerDashboard: () => apiJson('/customer/dashboard'),
      fetchCustomerOrders: () => apiJson('/customer/orders'),
    }),
    [token, user, loading, login, register, logout, refreshMe],
  );

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
}

export function useCustomer() {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error('useCustomer must be used within CustomerProvider');
  return ctx;
}
